import type { FC } from 'react'

import { InfoBox, LoaderWrapper, Popup, StyledText } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { Disclosure } from '@skytech/ui/Disclosure'
import { twJoin } from 'tailwind-merge'

import { useAppContext } from '@/app/index.context'
import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import type {
  EndOfLeaseAssetsValidate,
  ExtendAssetsValidate,
  ReturnAssetsValidate,
} from '@/resources/leasing'

import { useLeasedAssets } from '../AssetsLeased.context'

import type { LeasedAsset } from '../../resources'

export type ActionAssets = {
  message?: string
} & Pick<LeasedAsset, 'id' | 'assetDescription'>

export const ActionPopup: FC<{
  assets: LeasedAsset[]
}> = ({ assets }) => {
  const translate = useTranslation()
  const {
    action,
    isActionPopupOpen,
    selectedList,
    unavailableAssets,
    isLoadingValidation,
    isErrorValidation,
    isSuccessValidation,
    isLoadingConfirm,
    isErrorConfirm,
    isSuccessConfirm,
    onConfirmClick,
    clearSelectedList,
    setIsActionPopupOpen,
    resetMutations,
  } = useLeasedAssets()
  if (!action) throw Error(translate('Missing selected action'))

  const isConfirmDisabled = selectedList.length <= unavailableAssets.length
  const { actionTranslation, popupTitle, confirmSelectedItems } = getActionTranslations(
    action,
    selectedList.length
  )

  const handleOnClose = () => {
    resetMutations()
    clearSelectedList()
    setIsActionPopupOpen(false)
  }

  return (
    <Popup
      variant="medium"
      ariaLabel={translate('Confirm the action')}
      isOpen={isActionPopupOpen}
      onClose={() => handleOnClose()}
    >
      {popupTitle && <h2 className="center">{popupTitle}</h2>}
      <LoaderWrapper isLoading={isLoadingValidation}>
        {isErrorValidation && (
          <InfoBox emphasis="error" className="flex justify-center">
            <Translation id="API Error!" />
          </InfoBox>
        )}
        {isSuccessValidation &&
          (isSuccessConfirm ? (
            <UpdatedSuccess onClick={() => handleOnClose()} />
          ) : (
            <div className="flex flex-col gap-6">
              <ItemsInfo
                selectedListLength={selectedList.length}
                unavailableAssetsLength={unavailableAssets.length}
                actionTranslation={actionTranslation}
              />
              {unavailableAssets.length === 0 ? (
                <h3 className="center m-0 font-bold">{confirmSelectedItems}</h3>
              ) : (
                <UnavailableAssets assets={assets} unavailableAssetIds={unavailableAssets} />
              )}
              {isErrorConfirm && (
                <InfoBox emphasis="error" className="flex justify-center">
                  {actionTranslation.errorText}
                </InfoBox>
              )}
              <Buttons
                isConfirmDisabled={isConfirmDisabled}
                isLoadingConfirm={isLoadingConfirm}
                handleOnClose={handleOnClose}
                onClick={() => onConfirmClick()}
              />
            </div>
          ))}
      </LoaderWrapper>
    </Popup>
  )
}

const ItemsInfo: FC<{
  selectedListLength: number
  unavailableAssetsLength: number
  actionTranslation: TranslationType
}> = ({ selectedListLength, unavailableAssetsLength, actionTranslation }) => (
  <InfoBox emphasis="info" className="flex flex-col justify-around md:flex-row">
    <div className="my-2">
      <Translation id="Selected" />: {selectedListLength}
    </div>
    <div className="my-2">
      {actionTranslation.toBeExtendedReturned}: {selectedListLength - unavailableAssetsLength}
    </div>
    <div className="my-2">
      {actionTranslation.unableToExtendReturn}: {unavailableAssetsLength}
    </div>
  </InfoBox>
)

const UnavailableAssets: FC<{
  assets: LeasedAsset[]
  unavailableAssetIds: ExtendAssetsValidate | ReturnAssetsValidate | EndOfLeaseAssetsValidate
}> = ({ assets, unavailableAssetIds }) => {
  const unavailableAssets = assets?.filter(asset =>
    unavailableAssetIds?.some(item => item.assetId === asset.id)
  )
  return (
    <div>
      {unavailableAssets.map(asset => {
        const message = unavailableAssetIds.find(item => item.assetId === asset.id)?.message
        return (
          <Disclosure key={asset.id} defaultOpen={false} className="my-2">
            {({ open }) => (
              <div
                className={twJoin(
                  'transition-max-height border-0 border-b border-solid border-[#ccc] p-1',
                  open && 'max-h-[99999px]',
                  !open && 'max-h-48'
                )}
              >
                <Disclosure.Button isOpen={open}>
                  <div className="flex w-full items-center justify-between">
                    <StyledText block size="regular-plus" colorMode="black">
                      {asset.assetDescription}
                    </StyledText>
                  </div>
                </Disclosure.Button>

                <Disclosure.Panel className="whitespace-pre-line pl-1">
                  <StyledText block size="regular" colorMode="gray">
                    {message}
                  </StyledText>
                </Disclosure.Panel>
              </div>
            )}
          </Disclosure>
        )
      })}
    </div>
  )
}
const Buttons: FC<{
  isConfirmDisabled: boolean
  isLoadingConfirm: boolean
  handleOnClose: (open: boolean) => void
  onClick: () => void
}> = ({ isConfirmDisabled, isLoadingConfirm, handleOnClose, onClick }) => {
  const { whiteLabel } = useAppContext()
  const isDisabled = isLoadingConfirm || isConfirmDisabled

  return (
    <div className="flex justify-end">
      <Button variant="secondary" onClick={() => handleOnClose(false)}>
        <Translation id="Cancel" />
      </Button>

      <Button
        className={twJoin(
          'ml-8',
          isDisabled && whiteLabel === 'mobit' && 'disabled:brightness-100'
        )}
        variant="primary"
        disabled={isDisabled}
        onClick={e => {
          e.stopPropagation()
          onClick()
        }}
      >
        {isLoadingConfirm ? <Translation id="Saving..." /> : <Translation id="Confirm" />}
      </Button>
    </div>
  )
}

const UpdatedSuccess: FC<{ onClick: () => void }> = ({ onClick }) => (
  <div className="mt-10">
    <h3 className="center m-5">
      <Translation id="Selected assets successfully updated." />
    </h3>
    <div className="flex justify-center">
      <Button
        variant="secondary"
        onClick={e => {
          e.stopPropagation()
          onClick()
        }}
      >
        <Translation id="Close" />
      </Button>
    </div>
  </div>
)

const getActionTranslations = (action: string | number, count: number) => {
  let actionTranslation = null
  let popupTitle = null
  let confirmSelectedItems = null

  switch (action) {
    case 'EndOfLease': {
      actionTranslation = endOfLeaseTranslations
      popupTitle = endOfLeaseTranslations.popupTitle
      confirmSelectedItems = (
        <Translation
          id="Are you sure you want to end the lease for {count} {count, plural, one {asset} other {assets}}?"
          count={String(count)}
        />
      )
      break
    }
    case 'RETURN':
      actionTranslation = returnTranslations
      popupTitle = returnTranslations.popupTitle
      confirmSelectedItems = (
        <Translation
          id="Are you sure you want to return {count} {count, plural, one {asset} other {assets}}?"
          count={String(count)}
        />
      )
      break

    // Extend assets option
    default:
      actionTranslation = extendTranslations
      popupTitle = (
        <Translation
          id="Extend assets for {numberOfMonths} {numberOfMonths, plural, one {month} other {months}}"
          numberOfMonths={String(action)}
        />
      )
      confirmSelectedItems = (
        <Translation
          id="Are you sure you want to extent {count} {count, plural, one {asset} other {assets}}?"
          count={String(count)}
        />
      )
  }
  return { actionTranslation, popupTitle, confirmSelectedItems }
}

type TranslationType = {
  popupTitle: JSX.Element
  statusTitle: JSX.Element
  toBeExtendedReturned: JSX.Element
  unableToExtendReturn: JSX.Element
  errorText: JSX.Element
}

const endOfLeaseTranslations: TranslationType = {
  popupTitle: <Translation id="End of lease" />,
  statusTitle: <Translation id="Lease End Status" />,
  toBeExtendedReturned: <Translation id="To Be Ended" />,
  unableToExtendReturn: <Translation id="Unable to End Lease" />,
  errorText: <Translation id="We couldn't end the lease for assets. Please try again later." />,
}

const returnTranslations: TranslationType = {
  popupTitle: <Translation id="Return assets" />,
  statusTitle: <Translation id="Return status" />,
  toBeExtendedReturned: <Translation id="To be returned" />,
  unableToExtendReturn: <Translation id="Unable to return" />,
  errorText: <Translation id="We couldn't return assets. Please try again later." />,
}

const extendTranslations: TranslationType = {
  popupTitle: <Translation id="Extend assets" />,
  statusTitle: <Translation id="Extend status" />,
  toBeExtendedReturned: <Translation id="To be extended" />,
  unableToExtendReturn: <Translation id="Unable to extend" />,
  errorText: <Translation id="We couldn't extend assets. Please try again later." />,
}
